// src/components/event/CommentSection.js

import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

const CommentsContainer = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.text}20;
`;

const CommentTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.text};
`;

const CommentForm = styled.form`
  margin-bottom: 2rem;
  background: ${props => props.theme.card.background};
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  resize: vertical;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const SubmitButton = styled.button`
  padding: 0.8rem 1.5rem;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: ${props => props.theme.primaryDark || props.theme.primary + 'dd'};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  i {
    margin-right: 0.5rem;
  }
`;

const CommentsList = styled.div``;

const CommentItem = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: ${props => props.theme.card.shadow};
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const CommentAuthor = styled.div`
  font-weight: 500;
  color: ${props => props.theme.primary};
`;

const CommentDate = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
`;

const CommentText = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.6;
`;

const NoCommentsMessage = styled.p`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem;
  
  i {
    font-size: 2rem;
    color: ${props => props.theme.primary};
  }
`;

const CommentSection = ({ comments = [], loading = false, onAddComment, theme, t }) => {
  const [commentText, setCommentText] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Récupérer les valeurs stockées dans localStorage pour le formulaire
  useEffect(() => {
    const storedName = localStorage.getItem('comment_author_name');
    const storedEmail = localStorage.getItem('comment_author_email');
    
    if (storedName) setAuthorName(storedName);
    if (storedEmail) setAuthorEmail(storedEmail);
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!commentText.trim() || !authorName.trim()) return;
    
    try {
      setIsSubmitting(true);
      
      // Sauvegarder les informations de l'utilisateur dans localStorage
      localStorage.setItem('comment_author_name', authorName);
      if (authorEmail) localStorage.setItem('comment_author_email', authorEmail);
      
      await onAddComment(commentText, authorName, authorEmail);
      setCommentText('');
    } catch (err) {
      console.error("Error submitting comment:", err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString(navigator.language, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <CommentsContainer>
      <CommentTitle theme={theme}>
        {t('comments')} {comments.length > 0 && `(${comments.length})`}
      </CommentTitle>
      
      <CommentForm onSubmit={handleSubmit} theme={theme}>
        <TextArea 
          placeholder={t('leaveComment')}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          required
          theme={theme}
        />
        
        <FormRow>
          <FormGroup>
            <Label htmlFor="author_name" theme={theme}>{t('name')}</Label>
            <Input
              type="text"
              id="author_name"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder={t('yourName')}
              required
              theme={theme}
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="author_email" theme={theme}>{t('email')} ({t('optional')})</Label>
            <Input
              type="email"
              id="author_email"
              value={authorEmail}
              onChange={(e) => setAuthorEmail(e.target.value)}
              placeholder={t('yourEmail')}
              theme={theme}
            />
          </FormGroup>
        </FormRow>
        
        <SubmitButton 
          type="submit" 
          disabled={isSubmitting || !commentText.trim() || !authorName.trim()} 
          theme={theme}
        >
          {isSubmitting ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              {t('submitting')}
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane"></i>
              {t('postComment')}
            </>
          )}
        </SubmitButton>
      </CommentForm>
      
      <CommentsList>
        {loading ? (
          <LoadingSpinner theme={theme}>
            <i className="fas fa-spinner fa-spin"></i>
          </LoadingSpinner>
        ) : comments && comments.length > 0 ? (
          comments.map((comment, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <CommentItem theme={theme}>
                <CommentHeader>
                  <CommentAuthor theme={theme}>
                    {comment.author_name || comment.user?.username || t('anonymous')}
                  </CommentAuthor>
                  <CommentDate theme={theme}>
                    {formatDate(comment.created_at || new Date())}
                  </CommentDate>
                </CommentHeader>
                <CommentText theme={theme}>{comment.text}</CommentText>
              </CommentItem>
            </motion.div>
          ))
        ) : (
          <NoCommentsMessage theme={theme}>{t('noComments')}</NoCommentsMessage>
        )}
      </CommentsList>
    </CommentsContainer>
  );
};

export default CommentSection;